import type { ProjectFileResponseDto } from '@inference.engine/workspace-api-client/web';
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@monksflow/monks-ui';
import clsx from 'clsx';
import { Fragment, useId, useState } from 'react';
import {
  CheckCheckIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  MessageSquareIcon,
  MoreVerticalIcon,
  PencilIcon,
  TagsIcon,
  Trash2Icon,
  type LucideIcon,
} from 'lucide-react';
import { FileApprovalProgress } from '@/entities/asset-approval-chain';
import { AssetThumbnail } from '@/entities/project-file';
import { getNoPermissionMessage, PermissionTooltip } from '@/shared/ui';
import { AnimatedCount } from '@/shared/ui/AnimatedCount';
import { TruncatedText } from '@/shared/ui';
import { notificationService } from '@/lib/services/notification.service';
import { downloadFile } from '@/shared/utils/downloadFile';
import { formatShortDate } from '@/shared/utils/format';
import { FILE_STATUS_CONFIG } from '../../folders.constants';
import { useFileApprovalActions } from './useFileApprovalActions';
import { useTranslation } from 'react-i18next';

interface CardAction {
  key: string;
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  /** Refused without the project permission, and says so on hover. */
  guarded?: boolean;
  /** Draws the row in the destructive treatment. */
  destructive?: boolean;
  /** Heading above this item, when it opens a group. */
  groupLabel?: string;
}

interface Props {
  projectId: string;
  folderId: string;
  file: ProjectFileResponseDto;
  onClick: () => void;
  onRename?: (file: ProjectFileResponseDto) => void;
  onCopyLink?: (file: ProjectFileResponseDto) => void;
  onEditTags?: (file: ProjectFileResponseDto) => void;
  onDelete?: (file: ProjectFileResponseDto) => void;
  /** Disables the mutating actions (rename / edit tags / delete) with a permission tooltip. */
  actionsDisabled?: boolean;
  /**
   * Bulk-selection mode. The card then shows a checkbox over the preview and a
   * click toggles selection instead of navigating; the actions menu is hidden
   * to keep the selection gesture unambiguous.
   */
  /** True once something is selected: the card toggles rather than opens. */
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (file: ProjectFileResponseDto) => void;
}

export function FileAssetCard(props: Props) {
  const { t } = useTranslation('projects');

  const { file, selectionMode, selected, onToggleSelect } = props;
  const version = file.latestVersionNumber;

  // The approval steps are only worth fetching once the menu that shows them is
  // open, and never while selecting, when the menu is suppressed anyway.
  const menuId = useId();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const approval = useFileApprovalActions({
    projectId: props.projectId,
    folderId: props.folderId,
    fileId: file.id,
    hasChain: !!file.workflowProgress,
    menuOpen: isMenuOpen && !selectionMode,
  });

  const downloadUrl = file.downloadUrl as string | null | undefined;
  const [isDownloading, setIsDownloading] = useState(false);

  // S3 presigned URLs are cross-origin, where an anchor's `download` attribute
  // is ignored and the asset opens inline instead. Fetching the bytes first is
  // what makes the browser save rather than navigate.
  const handleDownload = async () => {
    if (!downloadUrl || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadFile({ url: downloadUrl, mimeType: file.fileType, fileName: file.fileName });
    } catch {
      notificationService.showError(t('projectDetail.fileAssetCard.failedToDownloadFile'));
    } finally {
      setIsDownloading(false);
    }
  };

  const statusLabel = t('projectDetail.fileAssetCard.status');

  // While selecting, the card is a selection target and its menu is out of
  // reach, so nothing it would offer is built.
  const downloadActions: CardAction[] =
    !selectionMode && downloadUrl
      ? [
          {
            key: 'download',
            label: t('projectDetail.fileAssetCard.download'),
            icon: DownloadIcon,
            onSelect: () => void handleDownload(),
            disabled: isDownloading,
          },
        ]
      : [];

  // A step the caller can act on is offered by name, so the menu says which
  // approval it is rather than just "approve".
  const approvalActions: CardAction[] = selectionMode
    ? []
    : approval.isLoading
      ? [
          {
            key: 'approval-loading',
            label: t('projectDetail.fileAssetCard.loadingApprovalSteps'),
            onSelect: () => undefined,
            disabled: true,
            groupLabel: statusLabel,
          },
        ]
      : approval.items.map((item) => {
          const approved = item.kind === 'approved';
          return {
            key: `approval-${item.stepName}`,
            label: approved
              ? t('projectDetail.fileAssetCard.stepApproved', { step: item.stepName })
              : t('projectDetail.fileAssetCard.approveStep', { step: item.stepName }),
            icon: approved ? CheckIcon : CheckCheckIcon,
            onSelect: () => approval.onSelect(item),
            disabled: approved || approval.isUpdating,
            groupLabel: statusLabel,
          };
        });

  const fileActions: CardAction[] = [];
  if (props.onRename) {
    fileActions.push({
      key: 'rename',
      label: t('projectDetail.fileAssetCard.rename'),
      icon: PencilIcon,
      onSelect: () => props.onRename?.(file),
      guarded: true,
    });
  }
  if (props.onCopyLink) {
    fileActions.push({
      key: 'copyLink',
      label: t('projectDetail.fileAssetCard.copyLink'),
      icon: CopyIcon,
      onSelect: () => props.onCopyLink?.(file),
    });
  }
  if (props.onEditTags) {
    fileActions.push({
      key: 'editTags',
      label: t('projectDetail.fileAssetCard.editTags'),
      icon: TagsIcon,
      onSelect: () => props.onEditTags?.(file),
      guarded: true,
    });
  }
  // A group of its own, which is what puts a rule above it: deleting is the one
  // row here that cannot be undone, and it sat flush against Edit tags.
  const destructiveActions: CardAction[] = props.onDelete
    ? [
        {
          key: 'delete',
          label: t('projectDetail.fileAssetCard.delete'),
          icon: Trash2Icon,
          onSelect: () => props.onDelete?.(file),
          guarded: true,
          destructive: true,
        },
      ]
    : [];

  const actionGroups = [downloadActions, approvalActions, fileActions, destructiveActions].filter(
    (group) => group.length > 0,
  );

  return (
    <div
      onClick={selectionMode && onToggleSelect ? () => onToggleSelect(file) : props.onClick}
      role="button"
      tabIndex={0}
      // Follows the click: while something is selected the card toggles, so the
      // keyboard has to agree with the pointer rather than always opening.
      onKeyDown={(e) => {
        // Keys aimed at the checkbox or the actions trigger are theirs. Acting
        // here cancels their own activation and navigates instead.
        if (e.target !== e.currentTarget) return;
        if (e.key !== 'Enter' && e.key !== ' ') return;
        // Space scrolls the grid otherwise, moving the card out from under the
        // press that just chose it.
        e.preventDefault();
        if (selectionMode && onToggleSelect) onToggleSelect(file);
        else props.onClick();
      }}
      className={clsx(
        'flex h-[306px] cursor-pointer flex-col overflow-hidden rounded-xl border bg-card p-1 transition-[border-color] duration-fast ease-out',
        // The library's own focus treatment rather than the bare outline it
        // falls back to, inset because the grid's scroll container starts at
        // the first row's edge. The 1px offset is the knob Tessra documents for
        // a ring sitting on a fill close to `--ring` — without it, focus on a
        // selected card just reads as a slightly thicker primary edge.
        'outline-none focus-visible:focus-ring focus-visible:ring-inset [--focus-ring-offset-width:1px]',
        // Inset, like the app's focus ring: the grid's scroll container starts
        // at the first row's top edge, so anything drawn outside a card's
        // border box is clipped away on that row.
        selected
          ? 'border-primary ring-1 ring-inset ring-primary'
          : 'border-border hover:border-border-strong',
      )}
    >
      <div className="relative h-[180px] shrink-0 overflow-hidden rounded-lg bg-muted">
        <AssetThumbnail file={file} className="h-full w-full" iconSize="size-18" fit="contain" />

        {version != null && (
          <span className="absolute right-3 top-3 rounded-md bg-black/65 px-2 py-0.5 text-xs font-medium text-white">
            {t('projectDetail.fileAssetCard.versionBadge', { versionNumber: version })}
          </span>
        )}

        {/* Always on offer, not revealed by a mode: the checkbox is how selection
            starts. Once something is selected the card itself toggles too, so
            picking a run of assets does not mean hitting each small target. */}
        {onToggleSelect && (
          <span className="absolute left-3 top-3 flex rounded-md bg-card/85 p-0.5">
            <Checkbox
              checked={!!selected}
              onCheckedChange={() => onToggleSelect(file)}
              // The card toggles too once something is selected; stop the event
              // so one click on the checkbox is not counted twice.
              onClick={(e) => e.stopPropagation()}
              aria-label={t('projectDetail.fileAssetCard.selectFile', { fileName: file.fileName })}
            />
          </span>
        )}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-xs p-md">
        <div className="flex min-w-0 items-start gap-xs">
          <TruncatedText
            value={file.fileName}
            className="flex-1 text-sm font-medium text-foreground"
          />
          {actionGroups.length > 0 && (
            <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="mini"
                  aria-label={t('projectDetail.fileAssetCard.fileActions')}
                  // Hidden rather than dropped while selecting: it is what the
                  // title truncates against and what sets that row's height, so
                  // removing it would re-cut every filename and lift the date
                  // beneath it.
                  className={`-mt-1 size-7 shrink-0 p-0 ${selectionMode ? 'invisible' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreVerticalIcon className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className={approvalActions.length > 0 ? 'w-56' : 'w-44'}
                onClick={(e) => e.stopPropagation()}
              >
                {actionGroups.map((group, groupIndex) => {
                  const labelId = `${menuId}-group-${groupIndex}`;
                  const items = group.map((action) => (
                    <PermissionTooltip
                      key={action.key}
                      disabled={!!(action.guarded && props.actionsDisabled)}
                      message={getNoPermissionMessage()}
                    >
                      <DropdownMenuItem
                        variant={action.destructive ? 'destructive' : 'default'}
                        onSelect={action.onSelect}
                        disabled={action.disabled || (action.guarded && props.actionsDisabled)}
                      >
                        {action.icon && <action.icon className="size-4" />}
                        {action.label}
                      </DropdownMenuItem>
                    </PermissionTooltip>
                  ));

                  return (
                    <Fragment key={group[0].key}>
                      {groupIndex > 0 && <DropdownMenuSeparator />}
                      {/* A heading only names its rows when the rows sit inside
                          a group that points back at it. */}
                      {group[0].groupLabel ? (
                        <DropdownMenuGroup aria-labelledby={labelId}>
                          <DropdownMenuLabel id={labelId}>{group[0].groupLabel}</DropdownMenuLabel>
                          {items}
                        </DropdownMenuGroup>
                      ) : (
                        items
                      )}
                    </Fragment>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <span className="text-body-sm text-muted-foreground">
          {formatShortDate(file.createdAt)}
        </span>

        <div className="mt-auto flex items-center gap-xs">
          {/* An asset no chain tracks still has a standing of its own, and the
              card is the only place in the grid that can say so. Without the
              fallback the row is empty for every asset the folder has not put
              on a chain, which is the state they arrive in. */}
          {file.workflowProgress ? (
            <FileApprovalProgress progress={file.workflowProgress} />
          ) : (
            <Badge variant={FILE_STATUS_CONFIG[file.status].variant} size="sm" roundness="full">
              {FILE_STATUS_CONFIG[file.status].label}
            </Badge>
          )}
          {file.commentCount > 0 && (
            <span className="flex items-center gap-3xs text-xs font-medium text-muted-foreground">
              <MessageSquareIcon className="size-4" />
              {/* The icon carries the meaning visually, so a screen reader gets the digit and its
                  noun as one phrase instead of a bare number after the status. */}
              <span aria-hidden="true">
                <AnimatedCount value={file.commentCount} animationKey={`file:${file.id}`} />
              </span>
              <span className="sr-only">
                {t('projectDetail.fileAssetCard.commentCount', { count: file.commentCount })}
              </span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
